import { REACT_APP_READER_URL } from 'env'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { compact } from 'lodash'
import { UploadFilesParams } from './Types'
import { ImageMenuSelectedImage, PROVIDERS_REQUIRING_DOWNLOAD } from './ImageMenuContext'
import { useStockVideoDownload } from 'hooks/queries/designer/useStockVideoDownload'
import { CreateMediaFileParams } from 'api/clients/MediaFileClient'
import { ensureUploadFilename } from 'utils/ensureUploadFilename'

interface UseUploadMediaProps {
  onUploadProgress?: (progress: number) => void
  setIsConfirming: (isConfirming: boolean) => void
  onUploadStart?: (files: File[]) => Promise<void>
  onUploadEnd?: () => void
  onUse?: (images: ImageMenuSelectedImage[]) => void
  createParams?: Partial<CreateMediaFileParams>
}

export const useUploadMedia = ({
  onUploadProgress,
  setIsConfirming,
  onUploadStart,
  onUploadEnd,
  onUse,
  createParams,
}: UseUploadMediaProps) => {
  const workspaceId = useCurrentWorkspaceId()
  const uploadsProjectId = useUploadsProject(workspaceId).data?.project?.id
  const { downloadHighRes } = useStockVideoDownload()

  const { uploadImageFilesWithoutLoadingToast } = useImportMediaFiles(
    false,
    () => onUploadProgress?.(0),
    onUploadProgress,
    { forceExtractBackgroundJobSync: true }, // need full metadata to generate designs
  )

  const uploadFilesFromDevice = async (fileList: FileList | null) => {
    if (!fileList || !uploadsProjectId) return
    setIsConfirming(true)

    const files = Array.from(fileList)
    await onUploadStart?.(Array.from(fileList))
    const uploadedFiles = await uploadFiles({ files, providers: files.map(() => 'device') })
    onUploadEnd?.()

    setIsConfirming(false)
    onUse?.(uploadedFiles)
  }

  const uploadFiles = async ({ files, providers }: UploadFilesParams) => {
    const uploadedImages = await uploadImageFilesWithoutLoadingToast(files, uploadsProjectId, createParams)

    return uploadedImages?.map((image, index) => ({
      ...image,
      url: image.source_url,
      source_url: image.source_url,
      thumbnail_url: image.thumbnail_url,
      provider: providers[index],
    })) as ImageMenuSelectedImage[]
  }

  const uploadImages = async (images: ImageMenuSelectedImage[]) => {
    setIsConfirming(true)

    // First we map the images to files so we can upload them to the server, keeping the original image object
    // so we can tell its provider. If the image is already in the server, we can just return it.
    const filesAndImages = await Promise.all(
      images.map(async (image) => {
        let url: string = image.url
        if (image.provider === 'link') {
          if (url.startsWith('http')) {
            url = `${REACT_APP_READER_URL}/proxy/image?url=${encodeURIComponent(url)}`
          }
        }

        if (PROVIDERS_REQUIRING_DOWNLOAD.includes(image.provider)) {
          if (image.provider === 'stockVideo' && image.id) {
            const highResUrl = await downloadHighRes(image.id)
            if (highResUrl) {
              url = highResUrl
            }
          }

          const response = await fetch(url)
          if (!response.ok) {
            throw new Error()
          }

          const blob = await response.blob()
          const mimeType = blob.type || 'image/png'
          const file = new File(
            [blob],
            ensureUploadFilename(image.original_filename || 'filename', mimeType),
            { type: mimeType },
          )

          return {
            ...image,
            file,
          }
        }

        return image
      }),
    )

    // Then we upload the files to the server, or return the original image if it's already in the server
    const filesToUpload = filesAndImages.map((fileOrImage) => fileOrImage.file).filter(Boolean) as File[]
    await onUploadStart?.(filesToUpload)

    const results = await Promise.all([
      ...(filesToUpload.length > 0
        ? await uploadFiles({
            files: filesToUpload,
            providers: filesAndImages.map((fileOrImage) => fileOrImage.provider),
          })
        : []),
      ...filesAndImages.filter((fileOrImage) => !fileOrImage.file),
    ])

    onUploadEnd?.()
    setIsConfirming(false)

    if (!results) return []
    return compact(results)
  }

  return { uploadImages, uploadFiles, uploadFilesFromDevice }
}
